/**
 * LES SIX RÉGIMES DE FACTURATION, repris tels quels de la V1 : ce sont les valeurs que
 * la colonne `bill.type` porte. Ils ne décident plus de la TVA : tout se facture TTC
 * (#544).
 *
 * ⚠️ UN MODULE À PART, SANS RIEN D'AUTRE. Le catalogue du panneau en tire les choix de
 * « Catégories proposées » ; les laisser dans `payment.ts` lui aurait fait charger le
 * client HTTP pour lire six libellés.
 */
/* `labelKey` : le mot du produit, que l'écran passe à `t()` avec `label` pour repli. */
export const REGIMES = [
  { type: 1, labelKey: "TAX_REGIME_FR_COMPANY", label: "Société en France" },
  {
    type: 2,
    labelKey: "TAX_REGIME_EU_COMPANY",
    label: "Société en Europe (hors France)",
  },
  {
    type: 3,
    labelKey: "TAX_REGIME_NON_EU_COMPANY",
    label: "Société hors Europe",
  },
  {
    type: 4,
    labelKey: "TAX_REGIME_INDIVIDUAL",
    label: "Particulier (France ou étranger)",
  },
  {
    type: 5,
    labelKey: "TAX_REGIME_LOCAL_AUTHORITY",
    label: "Collectivité territoriale",
  },
  {
    type: 6,
    labelKey: "TAX_REGIME_PUBLIC_BODY",
    label: "Établissement public",
  },
] as const;

export type Regime = (typeof REGIMES)[number];

/** La clé du réglage : la liste des régimes que le formulaire de paiement propose. */
export const REGIMES_KEY = "FO_MEETING_V2_PAY_REGIMES";

/**
 * La clé du NOM qu'un régime porte sur le site, quand l'organisateur le renomme.
 *
 * ⚠️ LE NOM SEUL, PAS LE RÉGIME. Renommer « Société en France » en « Entreprise
 * française » ne change pas le numéro rangé sur la facture : le serveur ne lit que
 * `type`.
 */
export const regimeLabelKey = (type: number): string =>
  `FO_MEETING_V2_PAY_REGIME_${type}_LABEL`;

/**
 * LE NOM QU'UN RÉGIME PORTE DEVANT LE PAYEUR : celui que l'organisateur a écrit, sinon
 * le mot du produit dans la langue lue (`say`, le `t()` de l'écran).
 *
 * ⚠️ UN NOM FAIT D'ESPACES N'EST PAS UN NOM : il laisserait une ligne blanche dans la
 * liste, et le payeur choisirait sans savoir quoi.
 */
export function regimeName(
  regime: Regime,
  named: unknown,
  say: (key: string, raw: string) => string,
): string {
  return typeof named === "string" && named.trim() !== ""
    ? named
    : say(regime.labelKey, regime.label);
}

/**
 * LES RÉGIMES PROPOSÉS, d'après ce que l'organisateur a coché.
 *
 * ⚠️ ABSENT VAUT TOUS. Un événement que personne n'a réglé doit garder la liste
 * entière, celle de la V1 : la vider ferait un formulaire impossible à valider.
 *
 * ⚠️ VIDE AUSSI VAUT TOUS, pour la même raison. Le panneau refuse de décocher le
 * dernier, mais une valeur écrite autrement (ou dont tous les numéros sont inconnus)
 * ne doit pas bloquer un payeur devant une liste sans choix.
 *
 * L'ordre reste celui de `REGIMES`, pas celui des clics : l'organisateur choisit
 * QUOI proposer, pas dans quel ordre.
 */
export function offeredRegimes(raw: unknown): ReadonlyArray<Regime> {
  if (!Array.isArray(raw)) return REGIMES;
  const picked = new Set(raw.map((v) => Number(v)));
  const kept = REGIMES.filter((r) => picked.has(r.type));
  return kept.length > 0 ? kept : REGIMES;
}

/**
 * LE RÉGIME RETENU POUR LA FACTURE, parmi ceux proposés.
 *
 * ⚠️ UN RÉGIME QUI N'EST PLUS PROPOSÉ NE PASSE PAS. Les coordonnées reprises de la
 * dernière facture portent le régime d'alors ; si l'organisateur l'a retiré depuis,
 * le payeur doit en choisir un autre, pas être facturé sous un régime invisible.
 *
 * ⚠️ UN SEUL PROPOSÉ, IL EST CHOISI D'OFFICE : demander de choisir dans une liste
 * d'une ligne est un clic pour rien.
 */
export function chosenRegime(
  type: number | undefined,
  offered: ReadonlyArray<Regime>,
): number | undefined {
  if (offered.some((r) => r.type === type)) return type;
  return offered.length === 1 ? offered[0]?.type : undefined;
}
